import { AvatarQuality, HeygenAvatarConfig } from "@models/avatar-config";
import {
  Configuration,
  NewSessionData,
  StreamingAvatarApi,
} from "@heygen/streaming-avatar";
import { VoiceDetectorFactory } from "@service/voice-detection/voice-detector.factory";
import { VoiceDetector } from "@service/voice-detection/voice-detector";
import sleep from "@utils/sleep";

let queue: HeygenAvatarService[] = [];

export class HeygenAvatarService {
  private avatar: StreamingAvatarApi | null = null;
  private voiceDetector: VoiceDetector | null = null;
  private sessionData: NewSessionData | null = null;

  running = false;
  stopping = false;
  stream: MediaStream | null = null;

  async start(
    avatarConfig: HeygenAvatarConfig,
    onChangeTalking: (talking: boolean) => void,
  ): Promise<void> {
    queue.push(this);

    while (queue[0] !== this) {
      await sleep(500);
    }

    await this.init(avatarConfig, onChangeTalking);
  }

  async stop(): Promise<void> {
    this.stopping = true;
    while (!this.running) {
      await sleep(500);
    }

    await this.destroy();
    queue = queue.slice(1);
    this.stopping = false;
  }

  async speak(text: string): Promise<void> {
    if (!this.avatar || !this.sessionData || !this.running || this.stopping) {
      return;
    }
    await this.avatar.speak({
      taskRequest: {
        text,
        sessionId: this.sessionData.sessionId,
      },
    });
  }

  /** Stops current speech without ending the Heygen session. */
  async stopSpeaking(): Promise<void> {
    if (!this.avatar || !this.sessionData || !this.running || this.stopping) {
      return;
    }
    try {
      await this.avatar.interrupt({
        interruptRequest: { sessionId: this.sessionData.sessionId },
      });
    } catch (e) {
      console.error(e);
    }
  }

  private async init(
    avatarConfig: HeygenAvatarConfig,
    onChangeTalking: (talking: boolean) => void,
  ): Promise<void> {
    try {
      const {
        accessToken,
        avatarConfig: { voiceId, avatarId },
        quality,
      } = avatarConfig;

      this.avatar = new StreamingAvatarApi(new Configuration({ accessToken }));

      this.sessionData = await this.avatar.createStartAvatar(
        {
          newSessionRequest: {
            quality: quality || AvatarQuality.Low,
            avatarName: avatarId,
            voice: { voiceId: voiceId },
          },
        },
        this.setDebug,
      );

      this.voiceDetector = VoiceDetectorFactory.create({
        stream: this.avatar.mediaStream,
        callback: ({ type }) => {
          onChangeTalking(type === "start");
        },
      });
      this.stream = this.avatar.mediaStream;
      this.running = true;
    } catch (e) {
      console.error(e);
    }
  }

  private async destroy(): Promise<void> {
    try {
      if (this.avatar && this.sessionData) {
        await this.avatar.stopAvatar(
          {
            stopSessionRequest: { sessionId: this.sessionData?.sessionId },
          },
          this.setDebug,
        );
      }

      if (this.voiceDetector) {
        this.voiceDetector.destroy();
      }
    } catch (e) {
      console.error(e);
    }
    this.running = false;
  }

  private setDebug() {}
}
